import { useCallback } from 'react'
import { actions } from '@/store'

export function useTodoMeta(todo, onUpdate) {
  const getRandomOffset = useCallback(() => {
    const buffer = new Uint32Array(1)
    crypto.getRandomValues(buffer)
    return (buffer[0] / 0xffffffff - 0.5) * 0.1
  }, [])
  const handleStartDateChange = useCallback(
    (date) => {
      const nextStartDate = date?.toISOString()
      const currentEndDate = todo.endDate ? new Date(todo.endDate) : null
      const hasEndBeforeStart = date && currentEndDate && currentEndDate.getTime() < date.getTime()

      onUpdate(todo.id, {
        startDate: nextStartDate,
        ...(hasEndBeforeStart ? { endDate: nextStartDate } : {}),
      })
    },
    [onUpdate, todo.endDate, todo.id],
  )

  const handleEndDateChange = useCallback(
    (date) => {
      onUpdate(todo.id, {
        endDate: date?.toISOString(),
      })
    },
    [onUpdate, todo.id],
  )

  const handleRecurrenceChange = useCallback(
    (recurrence) => {
      onUpdate(todo.id, {
        recurrence: recurrence === 'none' ? undefined : recurrence,
      })
    },
    [onUpdate, todo.id],
  )
  const handleLocationChange = useCallback(
    (e) => {
      // Keep for backward compatibility or simple quick-add if needed
      onUpdate(todo.id, {
        location: e.target.value,
      })
    },
    [onUpdate, todo.id],
  )

  const handleAddLocation = useCallback(
    (location) => {
      const lat = Number(location.lat)
      const lng = Number(location.lng)

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        console.warn('Skipping location add: invalid coordinates', location)
        return
      }

      actions.addLocation(todo.id, {
        id: crypto.randomUUID(),
        name: location.name || 'Unknown Location',
        lat,
        lng,
        address: location.address,
      })
    },
    [todo.id],
  )

  const handleRemoveLocation = useCallback((locationId) => {
    actions.removeLocation(locationId)
  }, [])

  const handleUpdateLocation = useCallback(
    (locationId, updates) => {
      // For now, simple update via onUpdate, but could be specific action
      const newLocations = (todo.locations || []).map((l) =>
        l.id === locationId ? { ...l, ...updates } : l,
      )
      onUpdate(todo.id, { locations: newLocations })
    },
    [onUpdate, todo.id, todo.locations],
  )

  const handleToggleAssignee = useCallback(
    (assigneeName) => {
      const currentAssignees = todo.assignees || []
      const isAlreadyAssigned = currentAssignees.includes(assigneeName)

      const newAssignees = isAlreadyAssigned
        ? currentAssignees.filter((name) => name !== assigneeName)
        : [...currentAssignees, assigneeName]

      onUpdate(todo.id, {
        assignees: newAssignees,
      })
    },
    [onUpdate, todo.assignees, todo.id],
  )

  return {
    handleStartDateChange,
    handleEndDateChange,
    handleRecurrenceChange,
    handleLocationChange,
    handleToggleAssignee,
    handleAddLocation,
    handleRemoveLocation,
    handleUpdateLocation,
  }
}
